import { useEffect, useMemo, useRef, useState } from 'react';
import {
  TextToSpeechApi,
  type SpeechMarker,
  type TextToSpeechDescriptor,
} from '../API/TextToSpeechApi';
import { AnalyticsEvent, useAnalytics } from '../Context/AnalyticsProvider';
import {
  AudioItem,
  AudioPlayerState,
  type AudioManager,
} from '../Context/AudioContext';

const ttsApi = new TextToSpeechApi();

export type TTSType =
  | 'article'
  | 'paragraph'
  | 'quiz_question'
  | 'quiz_option'
  | 'quiz_explanation'
  | 'dictionary';

interface TextToSpeechParams {
  descriptorUrl: string;
  groupId?: string;
  metadata?: Record<string, any>;
  manager?: AudioManager;
  state: AudioPlayerState;
  preload: boolean;
  delayMs?: number;
  onPlay?: () => void;
  onPause?: () => void;
  onEnd?: () => void;
  onCancel?: () => void;
  onError?: () => void;
  onWordStart?: (word: string, startIndex: number, endIndex: number) => void;
  analyticsContext?: {
    type: TTSType;
    articleId?: string;
    questionId?: string;
    optionId?: string;
    word?: string;
    paragraphIndex?: number;
  };
}

/**
 * @param groupId Playing a new TTS segment in the same group doesn't cancel TTS
 * @returns
 */
function TextToSpeech({
  descriptorUrl,
  groupId,
  metadata,
  manager,
  state,
  preload = false,
  delayMs = 0,
  onPlay,
  onPause,
  onEnd,
  onCancel,
  onError,
  onWordStart,
  analyticsContext,
}: TextToSpeechParams): JSX.Element | null {
  const { trackEvent } = useAnalytics();
  const [audio, setAudio] = useState<HTMLAudioElement>();
  const [markers, setMarkers] = useState<SpeechMarker[]>();
  const audioRef = useRef<HTMLAudioElement>();
  const markersRef = useRef<SpeechMarker[]>();
  audioRef.current = audio;
  markersRef.current = markers;

  function trackTTSEvent(action: 'start' | 'stop' | 'pause' | 'error') {
    if (!analyticsContext) return;

    const eventMap = {
      start: AnalyticsEvent.TEXT_TO_SPEECH_STARTED,
      stop: AnalyticsEvent.TEXT_TO_SPEECH_STOPPED,
      pause: AnalyticsEvent.TEXT_TO_SPEECH_PAUSED,
      error: AnalyticsEvent.TEXT_TO_SPEECH_ERROR,
    };

    trackEvent(eventMap[action], {
      type: analyticsContext.type,
      ...analyticsContext,
      timestamp: Date.now(),
    });
  }

  useEffect(() => {
    setAudio(undefined);
    setMarkers([]);
  }, [descriptorUrl]);

  const needsLoading = useMemo(() => {
    if (!descriptorUrl) return false;
    if (audio) return false;
    if (state !== AudioPlayerState.Play) {
      if (!preload) {
        return false;
      }
    }

    return true;
  }, [descriptorUrl, preload, audio, state]);

  useEffect(() => {
    if (!needsLoading) return;

    loadTTSMedia(descriptorUrl).then(({ audio, markers }) => {
      setAudio(audio);
      setMarkers(markers);
    });
  }, [descriptorUrl, needsLoading]);

  const prevStateRef = useRef<AudioPlayerState>(AudioPlayerState.Stop);

  useEffect(() => {
    if (!audio) return;

    const prevState = prevStateRef.current;
    prevStateRef.current = state;

    switch (state) {
      case AudioPlayerState.Play:
        play(delayMs);
        break;
      case AudioPlayerState.Pause:
        pause();
        break;
      case AudioPlayerState.Stop:
        stop(prevState === AudioPlayerState.Play);
        break;
    }
  }, [audio, state]);

  useEffect(() => {
    return () => {
      stop();
    };
  }, []);

  async function loadTTSMedia(descriptorUrl: string): Promise<{
    audio: HTMLAudioElement;
    markers: SpeechMarker[];
  }> {
    const descriptor = await ttsApi.getTTSDescriptor(descriptorUrl);
    const audio = createAudio(descriptor);
    audio.preload = preload ? 'auto' : 'none';

    manager?.register({
      src: descriptor.audioUrl,
      metadata: {
        ...metadata,
        groupId,
      },
      audio,
      managerStop: (newItem?: AudioItem) => {
        // Playing a new TTS segment in the same group doesn't cancel TTS altogether
        if (groupId && newItem?.metadata?.groupId === groupId) return;

        stop();
        onCancel?.();
      },
    });

    let markers: SpeechMarker[] = [];
    if (descriptor.markersUrl) {
      markers = await ttsApi.getSpeechMarkers(descriptor.markersUrl);
    }

    return { audio, markers };
  }

  function createAudio(
    ttsDescriptor: TextToSpeechDescriptor,
  ): HTMLAudioElement {
    const audio = new Audio(ttsDescriptor.audioUrl);
    audio.onplaying = () => {
      console.log('Audio started playing');
      manager?.onProgress(
        ttsDescriptor.audioUrl,
        audio.currentTime,
        audio.duration - audio.currentTime,
      );
      manager?.onPlay(ttsDescriptor.audioUrl);
      onPlay?.();
    };
    audio.onpause = () => {
      console.log('Audio paused');
      manager?.onPause();
      onPause?.();
    };
    audio.onended = () => {
      console.log('Audio ended');
      manager?.onProgress(
        ttsDescriptor.audioUrl,
        audio.currentTime,
        audio.duration - audio.currentTime,
      );
      onEnd?.();
    };
    audio.onerror = () => {
      console.error('Audio loading error:', {
        ttsDescriptor: ttsDescriptor,
        errorCode: audio.error?.code,
        errorMessage: audio.error?.message,
        currentTime: audio.currentTime,
        readyState: audio.readyState,
      });
      trackTTSEvent('error');
      onError?.();
    };
    audio.ontimeupdate = () => {
      if (!markersRef.current?.length) return;
      // Resetting the audio doesn't signify audio progress, we have other events to handle that.
      if (audio.currentTime === 0) return;

      if (onWordStart) {
        const wordBoundary = ttsApi.findWordBoundaryAtTime(
          audio.currentTime,
          markersRef.current,
        );
        if (wordBoundary) {
          onWordStart?.(
            wordBoundary.text,
            wordBoundary.startIndex,
            wordBoundary.endIndex,
          );
        }
      }
    };

    audio.crossOrigin = 'anonymous';
    return audio;
  }

  async function play(delayMs = 0) {
    trackTTSEvent('start');
    if (delayMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, delayMs));
    }

    const playPromise = audioRef.current?.play();

    if (playPromise !== undefined) {
      playPromise
        .then(function () {
          console.log('Audio playback started');
        })
        .catch(function (error) {
          console.error('Automatic playback failed:', error);
          onError?.();
        });
    }
  }

  function pause() {
    trackTTSEvent('pause');
    audioRef.current?.pause();
  }

  function stop(stopFromPlaying = false) {
    if (!audioRef.current) return;
    audioRef.current.pause();
    audioRef.current.currentTime = 0;
    if (stopFromPlaying) {
      trackTTSEvent('stop');
    }
  }

  return null;
}

export default TextToSpeech;
